import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import { environmentDetails, isMandatoryTagsFullfilled, orderId, quickConfigDetails, sspSubmitOrder, toggleSspModels } from 'pages/provisioning/store/storage';
import ProvisioningStyles from 'pages/provisioning/storage/style';
import { sspSubmitOrder as eiapSspSubmitOrder } from 'pages/provisioning/store/eip-stack';
import { eiapSave } from 'utils/constant';
import { navigateToWithPageReload } from 'utils';
import {
    formOrderObjFromCart,
    formOrderObjFromOrderDetails,
    formOrderObjFromEiapObject,
    resetEnvironmentDetails,
    resetOrder,
    resetToggleSspModal,
} from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/storage/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/storage/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/storage/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/storage/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/storage/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/storage/UpdatedToCartConfirmModal'));
const QuickConfig = React.lazy(() => import('pages/provisioning/storage/QuickConfig'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/storage/ShowCostDetails'));

const StorageProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [eiapOrder] = useRecoilState(eiapSspSubmitOrder);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const queryParams = new URLSearchParams(window.location.search);
    const componentId = queryParams.get('componentId');
    const axios = useAxios();

    const resourceGroupApi = useMutation((payload) => axios.get(endPoints.mySql.resourceGroup(payload)));
    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }
    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
        } else if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
            }));
        } else if (operation === eiapSave) {
            if (!eiapOrder?.appProfile || !eiapOrder?.landingZoneDetails || !eiapOrder?.sector || !eiapOrder?.environment || !eiapOrder?.region) {
                navigateToWithPageReload('/');
            }

            let orderObj;
            if (eiapOrder?.components?.[componentId]) {
                orderObj = await formOrderObjFromCart({ itemDetail: eiapOrder?.components?.[componentId] });
            } else {
                orderObj = await formOrderObjFromEiapObject(eiapOrder, data.cloudService);
            }
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data.cloudService,
                validationStatus: 'clearValidation',
                ...orderObj,
                componentId,
                eiapState: eiapOrder,
                operation: eiapSave,
                isTouched: false,
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }
    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetQuickConfig(undefined);
    };
    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>Azure Storage Account Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <QuickConfig />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default StorageProvisioningPage;
